/**
 * src/pages/BillingSettings.jsx
 * Gestión de suscripción: estado, portal Stripe, cancelación.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { getSuscripcion, abrirPortal } from '../api/billing'

const ESTADO_COLOR = {
  active:    'green',
  trialing:  'blue',
  past_due:  'yellow',
  canceled:  'gray',
}

export default function BillingSettings() {
  const [portalLoading, setPortalLoading] = useState(false)
  const { data: suscripcion, isLoading } = useQuery({
    queryKey: ['billing', 'suscripcion'],
    queryFn: getSuscripcion,
  })

  async function handlePortal() {
    setPortalLoading(true)
    try {
      const { url } = await abrirPortal()
      window.location.href = url
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al abrir el portal')
    } finally { setPortalLoading(false) }
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Facturación" subtitle="Gestión de suscripción y pagos" />
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px', maxWidth: '600px' }}>
        <Card title="Suscripción actual">
          {isLoading ? (
            <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>Cargando...</div>
          ) : !suscripcion?.tier ? (
            <div>
              <p style={{ color: 'var(--color-text-2)', fontSize: '14px', marginBottom: '16px' }}>No tienes una suscripción activa.</p>
              <Button onClick={() => window.location.href = '/pricing'}>Ver planes →</Button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                ['Plan', <span style={{ fontFamily: 'var(--font-mono)', textTransform: 'capitalize', fontSize: '13px' }}>{suscripcion.tier}</span>],
                ['Estado', <Badge color={ESTADO_COLOR[suscripcion.estado] || 'gray'} dot>{suscripcion.estado}</Badge>],
                ['Período hasta', suscripcion.periodo_fin ? new Date(suscripcion.periodo_fin).toLocaleDateString('es-CL') : '—'],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <span style={{ fontSize: '13px', color: 'var(--color-text-2)' }}>{label}</span>
                  <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>{value}</span>
                </div>
              ))}
              <div style={{ marginTop: '8px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <Button onClick={handlePortal} loading={portalLoading} variant="secondary"
                  icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>}
                >
                  Gestionar suscripción
                </Button>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
                Los cambios de plan, cancelación y facturas se gestionan desde el portal de Stripe.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
